import {
  useFloating,
  useTransitionStatus,
} from "@floating-ui/react"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import type { ToastOptions } from "./ToastOptions"
import { useToasterContext } from "./useToasterContext"

export function useToast({
  // these props are only for advanced use and generally can be skipped
  initialOpen = true,
  onOpenChange: setControlledOpen,
  open: controlledOpen,

  // these props change the appearance and animation
  stylePreset = "success",
  toastMs = 500,
  transitionMs = 500,

  // this prop is supplied by the Toaster
  id: toastId,
}: ToastOptions) {
  const [
    uncontrolledOpen,
    setUncontrolledOpen,
  ] = useState(initialOpen)

  const isOpen = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const hasOpenedRef = useRef<boolean>(false)
  const focusedRef = useRef<boolean>(false)
  const expiredRef = useRef<boolean>(false)

  const [
    labelId,
    setLabelId,
  ] = useState<string | undefined>()

  const [
    descriptionId,
    setDescriptionId,
  ] = useState<string | undefined>()

  const {
    updateToast,
    removeToast,
  } = useToasterContext()

  const data = useFloating({
    open: isOpen,
    onOpenChange: setOpen,
  })

  // this function prevents closing the notification when it is in focus
  const setFocused = useCallback(
    (focused: boolean) => {
      focusedRef.current = focused

      // if the notification is blurring and has expired then close it
      if (!focused && expiredRef.current) {
        expiredRef.current = false
        setOpen(false)
      }
    },
    [
      focusedRef,
      setOpen,
    ],
  )

  // the function closes the notification when the it times out
  const automatedClose = useCallback(
    () => {
      if (focusedRef.current) {
        // if the notification is currently focused then set it to expired but do
        // not clsoe it
        expiredRef.current = true
      } else {
        // close the notification
        setOpen(false)
      }
    },
    [
      setOpen,
      focusedRef,
    ],
  )

  // every time the toast opens, schedule the function to close it
  useEffect(
    () => {
      if (isOpen) {
        hasOpenedRef.current = true
        const dismissId = setTimeout(
          automatedClose,
          toastMs + (2 * transitionMs),
        )

        return () => {
          clearTimeout(dismissId)
        }
      }
    },
    [
      automatedClose,
      hasOpenedRef,
      isOpen,
      toastMs,
      transitionMs,
    ],
  )

  const context = data.context

  const transitionData = useTransitionStatus(
    context,
    { duration: transitionMs },
  )

  // update the status of this toast in the toaster
  useLayoutEffect(
    () => {
      updateToast(
        toastId,
        transitionData.status,
      )
    },
    [
      updateToast,
      transitionData.status,
      toastId,
    ],
  )

  // remove this toast from the toaster after it closes and unmounts
  useEffect(
    () => {
      if (!transitionData.isMounted && hasOpenedRef.current) {
        removeToast(toastId)
      }
    },
    [
      transitionData,
      removeToast,
      toastId,
    ],
  )

  return useMemo(
    () => ({
      ...data,
      ...transitionData,
      descriptionId,
      labelId,
      open: isOpen,
      setDescriptionId,
      setFocused,
      setLabelId,
      setOpen,
      stylePreset,
      toastId,
      transitionMs,
    }),
    [
      data,
      descriptionId,
      isOpen,
      labelId,
      setDescriptionId,
      setFocused,
      setLabelId,
      setOpen,
      stylePreset,
      toastId,
      transitionData,
      transitionMs,
    ],
  )
}
